import axios from 'axios';
import {
  SecurityScore,
  SecurityAlert,
  GuestDetail,
  GuestHistory,
  GuestSearchParams,
  PaginatedResponse,
  Guest,
  NationalityData,
  OccupancyData,
  GlobalStats,
} from '../types/security';
import { API_BASE_URL } from '../config/apiConfig';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global 401 handler — redirect to login on expired/invalid token
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Redirect to login unless already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const securityService = {
  // Get security score for a checkin
  getSecurityScore: async (checkinId: string): Promise<SecurityScore> => {
    const response = await api.get(`/api/v1/security/score/${checkinId}`);
    return response.data;
  },

  // Trigger security analysis
  analyzeCheckin: async (checkinId: string): Promise<SecurityScore> => {
    const response = await api.post(`/api/v1/security/analyze/${checkinId}`);
    return response.data;
  },

  // Get security alerts
  getAlerts: async (filters?: {
    severity?: string;
    status?: string;
    hotel_id?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<SecurityAlert[]> => {
    const response = await api.get('/api/v1/security/alerts', { params: filters });
    return response.data;
  },

  // Acknowledge alert
  acknowledgeAlert: async (alertId: string): Promise<SecurityAlert> => {
    const response = await api.put(`/api/v1/security/alerts/${alertId}/acknowledge`);
    return response.data;
  },

  // Get security statistics
  getStats: async (): Promise<any> => {
    const response = await api.get('/api/v1/security/stats');
    return response.data;
  },

  // Get scoring rules
  getScoringRules: async (): Promise<any> => {
    const response = await api.get('/api/v1/security/rules');
    return response.data;
  },

  // Update scoring rules
  updateScoringRules: async (rules: any): Promise<any> => {
    const response = await api.put('/api/v1/security/rules', rules);
    return response.data;
  },

  // --- New methods for dashboard finalization ---

  // Get guest detail by ID (via dashboard route for CNS/admin access)
  getGuestById: async (id: string): Promise<GuestDetail> => {
    const response = await api.get(`/api/v1/dashboard/guests/${id}`);
    return response.data;
  },

  // Get guest stay history (via dashboard route for CNS/admin access)
  getGuestHistory: async (id: string): Promise<GuestHistory> => {
    const response = await api.get(`/api/v1/dashboard/guests/${id}/history`);
    return response.data;
  },

  // Search guests with advanced params
  searchGuests: async (params: GuestSearchParams): Promise<PaginatedResponse<Guest>> => {
    const response = await api.get('/api/v1/dashboard/live-guests', { params });
    return response.data;
  },

  // Get live guests with filters (geographic + search)
  getLiveGuests: async (params: GuestSearchParams): Promise<PaginatedResponse<Guest>> => {
    const response = await api.get('/api/v1/dashboard/live-guests', { params });
    return response.data;
  },

  // Get nationality breakdown for charts
  getNationalityBreakdown: async (period?: string): Promise<NationalityData[]> => {
    const response = await api.get('/api/v1/dashboard/nationalities', {
      params: period ? { period } : undefined,
    });
    return response.data;
  },

  // Get occupancy rates (optionally by hotel)
  getOccupancyRates: async (hotelId?: string): Promise<OccupancyData> => {
    const response = await api.get('/api/v1/dashboard/occupancy', {
      params: hotelId ? { hotelId } : undefined,
    });
    return response.data;
  },

  // Get global stats (optionally by province)
  getGlobalStats: async (province?: string): Promise<GlobalStats> => {
    const response = await api.get('/api/v1/admin/dashboard/global-stats', {
      params: province ? { province } : undefined,
    });
    return response.data;
  },
};
